// src/store/driverSlice.js
import { createSlice, nanoid } from '@reduxjs/toolkit';

// Driver status constants
export const DRIVER_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  SUSPENDED: 'suspended',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  DELETED: 'deleted'
};

// Driver verification status constants
export const VERIFICATION_STATUS = {
  PENDING: 'pending',
  DOCUMENTS_REQUIRED: 'documents_required',
  UNDER_REVIEW: 'under_review',
  APPROVED: 'approved',
  REJECTED: 'rejected'
};

// Vehicle type constants
export const VEHICLE_TYPE = {
  SEDAN: 'sedan',
  SUV: 'suv',
  HATCHBACK: 'hatchback',
  LUXURY: 'luxury',
  ELECTRIC: 'electric'
};

// License type constants
export const LICENSE_TYPE = {
  REGULAR: 'regular',
  COMMERCIAL: 'commercial',
  CHAUFFEUR: 'chauffeur'
};

// Generate dummy driver data for development
const generateDummyDrivers = () => {
  const drivers = [];
  
  const dummyData = [
    {
      personalInfo: {
        firstName: 'John',
        lastName: 'Smith',
        email: 'john.smith@example.com',
        phone: '+1-555-0101',
        dateOfBirth: '1985-03-15',
        address: '123 Main St, New York, NY 10001',
        emergencyContact: {
          name: 'Jane Smith',
          phone: '+1-555-0102',
          relationship: 'Spouse'
        }
      },
      licenseInfo: {
        licenseNumber: 'DL123456789',
        licenseType: LICENSE_TYPE.COMMERCIAL,
        issueDate: '2020-01-15',
        expiryDate: '2025-01-15',
        issuingState: 'NY'
      },
      vehicleInfo: {
        make: 'Toyota',
        model: 'Camry',
        year: 2020,
        color: 'Silver',
        plateNumber: 'ABC-1234',
        vehicleType: VEHICLE_TYPE.SEDAN,
        registrationNumber: 'REG123456',
        insurancePolicyNumber: 'INS789012'
      },
      status: DRIVER_STATUS.APPROVED,
      verificationStatus: VERIFICATION_STATUS.APPROVED,
      rating: 4.8,
      totalTrips: 1250,
      totalEarnings: 45600.50,
      completionRate: 98.5,
      averageResponseTime: 2.3, // minutes
      customerSatisfaction: 4.9,
      onTimePercentage: 96.2,
      approvedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days ago
      licenseStatus: 'verified',
      backgroundCheck: 'passed',
      vehicleInspection: 'passed',
      driverLicense: { status: 'verified', expiryDate: '2025-01-15' },
      insurance: { status: 'verified', expiryDate: '2024-12-31' },
      vehicleRegistration: { status: 'verified', expiryDate: '2025-06-30' }
    },
    {
      personalInfo: {
        firstName: 'Maria',
        lastName: 'Garcia',
        email: 'maria.garcia@example.com',
        phone: '+1-555-0201',
        dateOfBirth: '1990-07-22',
        address: '456 Oak Ave, Los Angeles, CA 90210',
        emergencyContact: {
          name: 'Carlos Garcia',
          phone: '+1-555-0202',
          relationship: 'Brother'
        }
      },
      licenseInfo: {
        licenseNumber: 'DL987654321',
        licenseType: LICENSE_TYPE.COMMERCIAL,
        issueDate: '2019-05-10',
        expiryDate: '2024-05-10',
        issuingState: 'CA'
      },
      vehicleInfo: {
        make: 'Honda',
        model: 'Accord',
        year: 2019,
        color: 'Blue',
        plateNumber: 'XYZ-5678',
        vehicleType: VEHICLE_TYPE.SEDAN,
        registrationNumber: 'REG654321',
        insurancePolicyNumber: 'INS345678'
      },
      status: DRIVER_STATUS.ACTIVE,
      verificationStatus: VERIFICATION_STATUS.APPROVED,
      rating: 4.9,
      totalTrips: 890,
      totalEarnings: 32400.75,
      completionRate: 97.8,
      averageResponseTime: 1.8,
      customerSatisfaction: 4.8,
      onTimePercentage: 94.5,
      approvedAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(), // 45 days ago
      licenseStatus: 'verified',
      backgroundCheck: 'passed',
      vehicleInspection: 'passed',
      driverLicense: { status: 'verified', expiryDate: '2024-05-10' },
      insurance: { status: 'verified', expiryDate: '2024-11-15' },
      vehicleRegistration: { status: 'verified', expiryDate: '2025-03-20' }
    },
    {
      personalInfo: {
        firstName: 'David',
        lastName: 'Johnson',
        email: 'david.johnson@example.com',
        phone: '+1-555-0301',
        dateOfBirth: '1988-11-08',
        address: '789 Pine St, Chicago, IL 60601',
        emergencyContact: {
          name: 'Sarah Johnson',
          phone: '+1-555-0302',
          relationship: 'Wife'
        }
      },
      licenseInfo: {
        licenseNumber: 'DL456789123',
        licenseType: LICENSE_TYPE.COMMERCIAL,
        issueDate: '2021-02-20',
        expiryDate: '2026-02-20',
        issuingState: 'IL'
      },
      vehicleInfo: {
        make: 'Ford',
        model: 'Fusion',
        year: 2021,
        color: 'Black',
        plateNumber: 'DEF-9012',
        vehicleType: VEHICLE_TYPE.SEDAN,
        registrationNumber: 'REG789123',
        insurancePolicyNumber: 'INS901234'
      },
      status: DRIVER_STATUS.PENDING,
      verificationStatus: VERIFICATION_STATUS.UNDER_REVIEW,
      rating: 0,
      totalTrips: 0,
      totalEarnings: 0,
      completionRate: 0,
      averageResponseTime: 0,
      customerSatisfaction: 0,
      onTimePercentage: 0,
      licenseStatus: 'pending',
      backgroundCheck: 'pending',
      vehicleInspection: 'pending',
      driverLicense: { status: 'pending', expiryDate: '2026-02-20' },
      insurance: { status: 'pending', expiryDate: '2024-12-31' },
      vehicleRegistration: { status: 'pending', expiryDate: '2025-08-15' }
    },
    {
      personalInfo: {
        firstName: 'Sarah',
        lastName: 'Wilson',
        email: 'sarah.wilson@example.com',
        phone: '+1-555-0401',
        dateOfBirth: '1987-09-12',
        address: '321 Elm St, Miami, FL 33101',
        emergencyContact: {
          name: 'Tom Wilson',
          phone: '+1-555-0402',
          relationship: 'Husband'
        }
      },
      licenseInfo: {
        licenseNumber: 'DL789456123',
        licenseType: LICENSE_TYPE.COMMERCIAL,
        issueDate: '2020-08-15',
        expiryDate: '2025-08-15',
        issuingState: 'FL'
      },
      vehicleInfo: {
        make: 'Nissan',
        model: 'Altima',
        year: 2022,
        color: 'White',
        plateNumber: 'GHI-3456',
        vehicleType: VEHICLE_TYPE.SEDAN,
        registrationNumber: 'REG456789',
        insurancePolicyNumber: 'INS567890'
      },
      status: DRIVER_STATUS.APPROVED,
      verificationStatus: VERIFICATION_STATUS.APPROVED,
      rating: 4.95,
      totalTrips: 2100,
      totalEarnings: 78900.25,
      completionRate: 99.2,
      averageResponseTime: 1.5,
      customerSatisfaction: 4.98,
      onTimePercentage: 98.8,
      approvedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(), // 15 days ago
      licenseStatus: 'verified',
      backgroundCheck: 'passed',
      vehicleInspection: 'passed',
      driverLicense: { status: 'verified', expiryDate: '2025-08-15' },
      insurance: { status: 'verified', expiryDate: '2025-01-30' },
      vehicleRegistration: { status: 'verified', expiryDate: '2025-12-15' }
    },
    {
      personalInfo: {
        firstName: 'Mike',
        lastName: 'Brown',
        email: 'mike.brown@example.com',
        phone: '+1-555-0501',
        dateOfBirth: '1992-03-28',
        address: '654 Oak Ave, Seattle, WA 98101',
        emergencyContact: {
          name: 'Lisa Brown',
          phone: '+1-555-0502',
          relationship: 'Sister'
        }
      },
      licenseInfo: {
        licenseNumber: 'DL321654987',
        licenseType: LICENSE_TYPE.COMMERCIAL,
        issueDate: '2021-11-20',
        expiryDate: '2026-11-20',
        issuingState: 'WA'
      },
      vehicleInfo: {
        make: 'Hyundai',
        model: 'Elantra',
        year: 2021,
        color: 'Gray',
        plateNumber: 'JKL-7890',
        vehicleType: VEHICLE_TYPE.SEDAN,
        registrationNumber: 'REG987654',
        insurancePolicyNumber: 'INS234567'
      },
      status: DRIVER_STATUS.ACTIVE,
      verificationStatus: VERIFICATION_STATUS.APPROVED,
      rating: 3.8,
      totalTrips: 450,
      totalEarnings: 15600.75,
      completionRate: 82.5,
      averageResponseTime: 4.2,
      customerSatisfaction: 3.9,
      onTimePercentage: 78.3,
      approvedAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(), // 60 days ago
      licenseStatus: 'expired',
      backgroundCheck: 'passed',
      vehicleInspection: 'failed',
      driverLicense: { status: 'expired', expiryDate: '2023-11-20' },
      insurance: { status: 'verified', expiryDate: '2024-12-31' },
      vehicleRegistration: { status: 'expired', expiryDate: '2023-10-15' }
    },

    // Additional test drivers for comprehensive testing scenarios
    {
      personalInfo: {
        firstName: 'Rejected',
        lastName: 'Driver',
        email: 'rejected.driver@example.com',
        phone: '+1-555-0601',
        dateOfBirth: '1989-06-15',
        address: '987 Test St, Test City, TC 12345',
        emergencyContact: {
          name: 'Test Contact',
          phone: '+1-555-0602',
          relationship: 'Friend'
        }
      },
      licenseInfo: {
        licenseNumber: 'DL999888777',
        licenseType: LICENSE_TYPE.REGULAR,
        issueDate: '2020-01-01',
        expiryDate: '2025-01-01',
        issuingState: 'TC'
      },
      vehicleInfo: {
        make: 'Test',
        model: 'Vehicle',
        year: 2018,
        color: 'Red',
        plateNumber: 'TEST-001',
        vehicleType: VEHICLE_TYPE.SEDAN,
        registrationNumber: 'REGTEST001',
        insurancePolicyNumber: 'INSTEST001'
      },
      status: DRIVER_STATUS.REJECTED,
      verificationStatus: VERIFICATION_STATUS.REJECTED,
      rating: 0,
      totalTrips: 0,
      totalEarnings: 0,
      completionRate: 0,
      averageResponseTime: 0,
      customerSatisfaction: 0,
      onTimePercentage: 0,
      rejectionReason: 'Failed background check',
      rejectedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
      licenseStatus: 'rejected',
      backgroundCheck: 'failed',
      vehicleInspection: 'not_required',
      driverLicense: { status: 'rejected', expiryDate: '2025-01-01' },
      insurance: { status: 'not_verified', expiryDate: '' },
      vehicleRegistration: { status: 'not_verified', expiryDate: '' }
    },
    {
      personalInfo: {
        firstName: 'Suspended',
        lastName: 'Driver',
        email: 'suspended.driver@example.com',
        phone: '+1-555-0701',
        dateOfBirth: '1986-12-20',
        address: '456 Suspend Ave, Test City, TC 12345',
        emergencyContact: {
          name: 'Emergency Contact',
          phone: '+1-555-0702',
          relationship: 'Spouse'
        }
      },
      licenseInfo: {
        licenseNumber: 'DL777666555',
        licenseType: LICENSE_TYPE.COMMERCIAL,
        issueDate: '2019-06-15',
        expiryDate: '2024-06-15',
        issuingState: 'TC'
      },
      vehicleInfo: {
        make: 'Suspended',
        model: 'Car',
        year: 2019,
        color: 'Yellow',
        plateNumber: 'SUSP-002',
        vehicleType: VEHICLE_TYPE.SEDAN,
        registrationNumber: 'REGSUSP002',
        insurancePolicyNumber: 'INSSUSP002'
      },
      status: DRIVER_STATUS.SUSPENDED,
      verificationStatus: VERIFICATION_STATUS.APPROVED,
      rating: 4.2,
      totalTrips: 320,
      totalEarnings: 12800.00,
      completionRate: 85.0,
      averageResponseTime: 3.5,
      customerSatisfaction: 4.0,
      onTimePercentage: 82.0,
      suspensionReason: 'Multiple customer complaints',
      suspendedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
      approvedAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days ago
      licenseStatus: 'verified',
      backgroundCheck: 'passed',
      vehicleInspection: 'passed',
      driverLicense: { status: 'verified', expiryDate: '2024-06-15' },
      insurance: { status: 'verified', expiryDate: '2024-12-31' },
      vehicleRegistration: { status: 'verified', expiryDate: '2025-03-15' }
    },
    {
      personalInfo: {
        firstName: 'Inactive',
        lastName: 'Driver',
        email: 'inactive.driver@example.com',
        phone: '+1-555-0801',
        dateOfBirth: '1991-04-10',
        address: '789 Inactive Rd, Test City, TC 12345',
        emergencyContact: {
          name: 'Family Member',
          phone: '+1-555-0802',
          relationship: 'Sibling'
        }
      },
      licenseInfo: {
        licenseNumber: 'DL555444333',
        licenseType: LICENSE_TYPE.COMMERCIAL,
        issueDate: '2021-03-10',
        expiryDate: '2026-03-10',
        issuingState: 'TC'
      },
      vehicleInfo: {
        make: 'Inactive',
        model: 'Vehicle',
        year: 2020,
        color: 'Blue',
        plateNumber: 'INAC-003',
        vehicleType: VEHICLE_TYPE.SUV,
        registrationNumber: 'REGINAC003',
        insurancePolicyNumber: 'INSINAC003'
      },
      status: DRIVER_STATUS.INACTIVE,
      verificationStatus: VERIFICATION_STATUS.APPROVED,
      rating: 4.6,
      totalTrips: 150,
      totalEarnings: 5400.00,
      completionRate: 92.0,
      averageResponseTime: 2.8,
      customerSatisfaction: 4.5,
      onTimePercentage: 88.0,
      approvedAt: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString(), // 120 days ago
      lastActive: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days ago
      licenseStatus: 'verified',
      backgroundCheck: 'passed',
      vehicleInspection: 'passed',
      driverLicense: { status: 'verified', expiryDate: '2026-03-10' },
      insurance: { status: 'verified', expiryDate: '2025-02-28' },
      vehicleRegistration: { status: 'verified', expiryDate: '2025-09-15' }
    }
  ];

  dummyData.forEach((data, index) => {
    drivers.push({
      id: nanoid(),
      ...data,
      registrationDate: new Date(Date.now() - (index * 7 * 24 * 60 * 60 * 1000)).toISOString(),
      lastActive: new Date(Date.now() - (index * 2 * 60 * 60 * 1000)).toISOString(),
      documents: {
        profilePhoto: `/images/drivers/driver-${index + 1}.jpg`,
        licensePhoto: `/images/documents/license-${index + 1}.jpg`,
        vehicleRegistration: `/images/documents/registration-${index + 1}.jpg`,
        insurance: `/images/documents/insurance-${index + 1}.jpg`
      },
      bankingInfo: {
        accountHolderName: `${data.personalInfo.firstName} ${data.personalInfo.lastName}`,
        accountNumber: `****${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`,
        routingNumber: `****${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`,
        bankName: ['Chase Bank', 'Bank of America', 'Wells Fargo'][index % 3]
      },
      preferences: {
        maxDistance: 25,
        workingHours: {
          start: '06:00',
          end: '22:00'
        },
        acceptSharedRides: true,
        acceptLongDistance: true
      }
    });
  });

  return drivers;
};



// Generate dummy pending registrations for development
const generateDummyPendingRegistrations = () => {
  return [
    {
      id: nanoid(),
      name: 'Michael Johnson',
      email: 'michael.johnson@example.com',
      phone: '+1-555-0201',
      appliedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
      status: DRIVER_STATUS.PENDING,
      verificationStatus: VERIFICATION_STATUS.PENDING,
      vehicleInfo: {
        make: 'Toyota',
        model: 'Camry',
        year: 2020,
        plateNumber: 'ABC123'
      },
      personalInfo: {
        firstName: 'Michael',
        lastName: 'Johnson',
        dateOfBirth: '1988-07-22',
        address: '123 Main St, City, State 12345'
      }
    },
    {
      id: nanoid(),
      name: 'Sarah Williams',
      email: 'sarah.williams@example.com',
      phone: '+1-555-0202',
      appliedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
      status: DRIVER_STATUS.PENDING,
      verificationStatus: VERIFICATION_STATUS.DOCUMENTS_REQUIRED,
      vehicleInfo: {
        make: 'Honda',
        model: 'Civic',
        year: 2019,
        plateNumber: 'XYZ789'
      },
      personalInfo: {
        firstName: 'Sarah',
        lastName: 'Williams',
        dateOfBirth: '1992-11-15',
        address: '456 Oak Ave, City, State 12345'
      }
    },
    {
      id: nanoid(),
      name: 'David Brown',
      email: 'david.brown@example.com',
      phone: '+1-555-0203',
      appliedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
      status: DRIVER_STATUS.PENDING,
      verificationStatus: VERIFICATION_STATUS.UNDER_REVIEW,
      vehicleInfo: {
        make: 'Ford',
        model: 'Focus',
        year: 2021,
        plateNumber: 'DEF456'
      },
      personalInfo: {
        firstName: 'David',
        lastName: 'Brown',
        dateOfBirth: '1985-04-08',
        address: '789 Pine St, City, State 12345'
      }
    }
  ];
};

const initialState = {
  drivers: generateDummyDrivers(),
  pendingRegistrations: generateDummyPendingRegistrations(),
  isLoading: false,
  error: null,
  filters: {
    status: 'all',
    verificationStatus: 'all',
    searchTerm: ''
  }
};

// Driver slice with comprehensive actions
const driverSlice = createSlice({
  name: 'drivers',
  initialState,
  reducers: {
    // Driver registration actions
    registerDriver: {
      reducer(state, action) {
        state.pendingRegistrations.push(action.payload);
        state.isLoading = false;
      },
      prepare(driverData) {
        return {
          payload: {
            id: nanoid(),
            ...driverData,
            status: DRIVER_STATUS.PENDING,
            verificationStatus: VERIFICATION_STATUS.PENDING,
            registrationDate: new Date().toISOString(),
            rating: 0,
            totalTrips: 0,
            totalEarnings: 0,
            lastActive: new Date().toISOString()
          }
        };
      }
    },

    // Admin verification actions
    approveDriver(state, action) {
      const { driverId, adminNotes } = action.payload;
      
      // Move from pending to approved drivers
      const pendingIndex = state.pendingRegistrations.findIndex(d => d.id === driverId);
      if (pendingIndex !== -1) {
        const driver = state.pendingRegistrations[pendingIndex];
        driver.status = DRIVER_STATUS.APPROVED;
        driver.verificationStatus = VERIFICATION_STATUS.APPROVED;
        driver.approvedDate = new Date().toISOString();
        driver.adminNotes = adminNotes;
        
        state.drivers.push(driver);
        state.pendingRegistrations.splice(pendingIndex, 1);
      }
    },

    rejectDriver(state, action) {
      const { driverId, reason, adminNotes } = action.payload;
      
      const driver = state.pendingRegistrations.find(d => d.id === driverId);
      if (driver) {
        driver.status = DRIVER_STATUS.REJECTED;
        driver.verificationStatus = VERIFICATION_STATUS.REJECTED;
        driver.rejectionReason = reason;
        driver.adminNotes = adminNotes;
        driver.rejectedDate = new Date().toISOString();
      }
    },

    // Driver status management
    updateDriverStatus(state, action) {
      const { driverId, status } = action.payload;
      const driver = state.drivers.find(d => d.id === driverId);
      if (driver) {
        driver.status = status;
        driver.lastStatusUpdate = new Date().toISOString();
      }
    },

    // Driver profile updates
    updateDriverProfile(state, action) {
      const { driverId, updates } = action.payload;
      const driver = state.drivers.find(d => d.id === driverId);
      if (driver) {
        Object.assign(driver, updates);
        driver.lastUpdated = new Date().toISOString();
      }
    },



    // Loading and error states
    setLoading(state, action) {
      state.isLoading = action.payload;
    },

    setError(state, action) {
      state.error = action.payload;
      state.isLoading = false;
    },

    clearError(state) {
      state.error = null;
    },

    // Filter actions
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },

    clearFilters(state) {
      state.filters = {
        status: 'all',
        verificationStatus: 'all',
        searchTerm: ''
      };
    },



    // Driver statistics updates
    updateDriverStats(state, action) {
      const { driverId, stats } = action.payload;
      const driver = state.drivers.find(d => d.id === driverId);
      if (driver) {
        if (stats.totalTrips !== undefined) driver.totalTrips = stats.totalTrips;
        if (stats.totalEarnings !== undefined) driver.totalEarnings = stats.totalEarnings;
        if (stats.rating !== undefined) driver.rating = stats.rating;
        driver.lastActive = new Date().toISOString();
      }
    },

    // Document management
    updateDriverDocuments(state, action) {
      const { driverId, documents } = action.payload;
      const driver = state.drivers.find(d => d.id === driverId) ||
                    state.pendingRegistrations.find(d => d.id === driverId);

      if (driver) {
        driver.documents = { ...driver.documents, ...documents };
        driver.lastUpdated = new Date().toISOString();
      }
    },

    // Advanced verification workflow actions
    requestDocumentReview(state, action) {
      const { driverId, documentType, adminNotes } = action.payload;
      const driver = state.pendingRegistrations.find(d => d.id === driverId);

      if (driver) {
        driver.verificationStatus = VERIFICATION_STATUS.DOCUMENTS_REQUIRED;
        driver.requiredDocuments = driver.requiredDocuments || [];
        if (!driver.requiredDocuments.includes(documentType)) {
          driver.requiredDocuments.push(documentType);
        }
        driver.adminNotes = adminNotes;
        driver.lastReviewDate = new Date().toISOString();
      }
    },

    markDocumentAsReviewed(state, action) {
      const { driverId, documentType, approved, reviewNotes } = action.payload;
      const driver = state.pendingRegistrations.find(d => d.id === driverId);

      if (driver) {
        driver.documentReviews = driver.documentReviews || {};
        driver.documentReviews[documentType] = {
          approved,
          reviewNotes,
          reviewedAt: new Date().toISOString()
        };

        // Check if all required documents are reviewed and approved
        const allDocumentsApproved = driver.requiredDocuments?.every(docType =>
          driver.documentReviews[docType]?.approved
        );

        if (allDocumentsApproved) {
          driver.verificationStatus = VERIFICATION_STATUS.UNDER_REVIEW;
        }
      }
    },

    scheduleDriverInterview(state, action) {
      const { driverId, interviewDate, interviewType, adminNotes } = action.payload;
      const driver = state.pendingRegistrations.find(d => d.id === driverId);

      if (driver) {
        driver.interview = {
          scheduledDate: interviewDate,
          type: interviewType, // 'phone', 'video', 'in-person'
          status: 'scheduled',
          adminNotes
        };
        driver.verificationStatus = VERIFICATION_STATUS.UNDER_REVIEW;
      }
    },

    completeDriverInterview(state, action) {
      const { driverId, passed, interviewNotes, score } = action.payload;
      const driver = state.pendingRegistrations.find(d => d.id === driverId);

      if (driver && driver.interview) {
        driver.interview.status = 'completed';
        driver.interview.passed = passed;
        driver.interview.notes = interviewNotes;
        driver.interview.score = score;
        driver.interview.completedAt = new Date().toISOString();

        if (passed) {
          driver.verificationStatus = VERIFICATION_STATUS.APPROVED;
        } else {
          driver.verificationStatus = VERIFICATION_STATUS.REJECTED;
          driver.status = DRIVER_STATUS.REJECTED;
        }
      }
    },

    addVerificationNote(state, action) {
      const { driverId, note, adminId } = action.payload;
      const driver = state.pendingRegistrations.find(d => d.id === driverId) ||
                    state.drivers.find(d => d.id === driverId);

      if (driver) {
        driver.verificationNotes = driver.verificationNotes || [];
        driver.verificationNotes.push({
          id: nanoid(),
          note,
          adminId,
          timestamp: new Date().toISOString()
        });
      }
    },

    bulkUpdateDriverStatus(state, action) {
      const { driverIds, status, reason } = action.payload;

      driverIds.forEach(driverId => {
        const driver = state.drivers.find(d => d.id === driverId);
        if (driver) {
          driver.status = status;
          driver.bulkUpdateReason = reason;
          driver.lastStatusUpdate = new Date().toISOString();
        }
      });
    },

    // CRUD Operations - API-ready async action patterns

    // Create Driver
    addDriverStart(state) {
      state.isLoading = true;
      state.error = null;
    },

    addDriverSuccess(state, action) {
      state.isLoading = false;
      state.drivers.push(action.payload);
      state.error = null;
    },

    addDriverFailure(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Update Driver
    updateDriverStart(state) {
      state.isLoading = true;
      state.error = null;
    },

    updateDriverSuccess(state, action) {
      state.isLoading = false;
      const { driverId, updates } = action.payload;
      const driverIndex = state.drivers.findIndex(d => d.id === driverId);
      if (driverIndex !== -1) {
        state.drivers[driverIndex] = {
          ...state.drivers[driverIndex],
          ...updates,
          lastUpdated: new Date().toISOString()
        };
      }
      // Also update in pending registrations if exists
      const pendingIndex = state.pendingRegistrations.findIndex(d => d.id === driverId);
      if (pendingIndex !== -1) {
        state.pendingRegistrations[pendingIndex] = {
          ...state.pendingRegistrations[pendingIndex],
          ...updates,
          lastUpdated: new Date().toISOString()
        };
      }
      state.error = null;
    },

    updateDriverFailure(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Delete Driver (Soft Delete)
    deleteDriverStart(state) {
      state.isLoading = true;
      state.error = null;
    },

    deleteDriverSuccess(state, action) {
      state.isLoading = false;
      const { driverId, softDelete = true } = action.payload;

      if (softDelete) {
        // Soft delete - mark as deleted but keep in state
        const driver = state.drivers.find(d => d.id === driverId);
        if (driver) {
          driver.status = DRIVER_STATUS.DELETED;
          driver.deletedAt = new Date().toISOString();
          driver.isDeleted = true;
        }
        const pendingDriver = state.pendingRegistrations.find(d => d.id === driverId);
        if (pendingDriver) {
          pendingDriver.status = DRIVER_STATUS.DELETED;
          pendingDriver.deletedAt = new Date().toISOString();
          pendingDriver.isDeleted = true;
        }
      } else {
        // Hard delete - remove from state
        state.drivers = state.drivers.filter(d => d.id !== driverId);
        state.pendingRegistrations = state.pendingRegistrations.filter(d => d.id !== driverId);
      }
      state.error = null;
    },

    deleteDriverFailure(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Restore Driver (for soft deleted drivers)
    restoreDriverStart(state) {
      state.isLoading = true;
      state.error = null;
    },

    restoreDriverSuccess(state, action) {
      state.isLoading = false;
      const { driverId } = action.payload;

      const driver = state.drivers.find(d => d.id === driverId);
      if (driver && driver.isDeleted) {
        driver.status = DRIVER_STATUS.ACTIVE;
        driver.restoredAt = new Date().toISOString();
        delete driver.isDeleted;
        delete driver.deletedAt;
      }

      const pendingDriver = state.pendingRegistrations.find(d => d.id === driverId);
      if (pendingDriver && pendingDriver.isDeleted) {
        pendingDriver.status = DRIVER_STATUS.PENDING;
        pendingDriver.restoredAt = new Date().toISOString();
        delete pendingDriver.isDeleted;
        delete pendingDriver.deletedAt;
      }
      state.error = null;
    },

    restoreDriverFailure(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Optimistic Updates for better UX
    optimisticUpdateDriver(state, action) {
      const { driverId, updates } = action.payload;
      const driver = state.drivers.find(d => d.id === driverId) ||
                    state.pendingRegistrations.find(d => d.id === driverId);
      if (driver) {
        Object.assign(driver, updates);
        driver.lastUpdated = new Date().toISOString();
        driver._optimistic = true; // Mark as optimistic update
      }
    },

    revertOptimisticUpdate(state, action) {
      const { driverId, originalData } = action.payload;
      const driver = state.drivers.find(d => d.id === driverId) ||
                    state.pendingRegistrations.find(d => d.id === driverId);
      if (driver && driver._optimistic) {
        Object.assign(driver, originalData);
        delete driver._optimistic;
      }
    },

    confirmOptimisticUpdate(state, action) {
      const { driverId } = action.payload;
      const driver = state.drivers.find(d => d.id === driverId) ||
                    state.pendingRegistrations.find(d => d.id === driverId);
      if (driver && driver._optimistic) {
        delete driver._optimistic;
      }
    }
  }
});

export const {
  registerDriver,
  approveDriver,
  rejectDriver,
  updateDriverStatus,
  updateDriverProfile,
  setLoading,
  setError,
  clearError,
  setFilters,
  clearFilters,
  updateDriverStats,
  updateDriverDocuments,
  requestDocumentReview,
  markDocumentAsReviewed,
  scheduleDriverInterview,
  completeDriverInterview,
  addVerificationNote,
  bulkUpdateDriverStatus,
  // CRUD Actions
  addDriverStart,
  addDriverSuccess,
  addDriverFailure,
  updateDriverStart,
  updateDriverSuccess,
  updateDriverFailure,
  deleteDriverStart,
  deleteDriverSuccess,
  deleteDriverFailure,
  restoreDriverStart,
  restoreDriverSuccess,
  restoreDriverFailure,
  optimisticUpdateDriver,
  revertOptimisticUpdate,
  confirmOptimisticUpdate
} = driverSlice.actions;

export default driverSlice.reducer;

// Async Thunk Actions for API Integration
// These functions simulate API calls and can be easily replaced with actual API calls

export const addDriver = (driverData) => async (dispatch) => {
  try {
    dispatch(addDriverStart());

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Prepare driver data with required fields
    const newDriver = {
      id: nanoid(),
      ...driverData,
      status: DRIVER_STATUS.PENDING,
      verificationStatus: VERIFICATION_STATUS.PENDING,
      registrationDate: new Date().toISOString(),
      rating: 0,
      totalTrips: 0,
      totalEarnings: 0,
      lastActive: new Date().toISOString(),
      completionRate: 0,
      averageResponseTime: 0,
      customerSatisfaction: 0,
      onTimePercentage: 0,
      licenseStatus: 'pending',
      backgroundCheck: 'pending',
      vehicleInspection: 'pending',
      driverLicense: { status: 'pending', expiryDate: driverData.licenseInfo?.expiryDate || '' },
      insurance: { status: 'pending', expiryDate: '' },
      vehicleRegistration: { status: 'pending', expiryDate: '' }
    };

    // In a real app, this would be an API call:
    // const response = await api.post('/drivers', newDriver);
    // dispatch(addDriverSuccess(response.data));

    dispatch(addDriverSuccess(newDriver));
    return { success: true, data: newDriver };
  } catch (error) {
    dispatch(addDriverFailure(error.message || 'Failed to add driver'));
    return { success: false, error: error.message };
  }
};

export const updateDriver = (driverId, updates) => async (dispatch, getState) => {
  try {
    dispatch(updateDriverStart());

    // Get current driver data for optimistic updates
    const state = getState();
    const currentDriver = state.drivers.drivers.find(d => d.id === driverId) ||
                         state.drivers.pendingRegistrations.find(d => d.id === driverId);

    if (!currentDriver) {
      throw new Error('Driver not found');
    }

    // Apply optimistic update
    dispatch(optimisticUpdateDriver({ driverId, updates }));

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 800));

    // In a real app, this would be an API call:
    // const response = await api.put(`/drivers/${driverId}`, updates);
    // dispatch(updateDriverSuccess({ driverId, updates: response.data }));

    dispatch(updateDriverSuccess({ driverId, updates }));
    dispatch(confirmOptimisticUpdate({ driverId }));

    return { success: true, data: { ...currentDriver, ...updates } };
  } catch (error) {
    // Revert optimistic update on error
    dispatch(revertOptimisticUpdate({
      driverId,
      originalData: getState().drivers.drivers.find(d => d.id === driverId) ||
                   getState().drivers.pendingRegistrations.find(d => d.id === driverId)
    }));
    dispatch(updateDriverFailure(error.message || 'Failed to update driver'));
    return { success: false, error: error.message };
  }
};

export const deleteDriver = (driverId, options = {}) => async (dispatch) => {
  try {
    dispatch(deleteDriverStart());

    const { softDelete = true, reason = '' } = options;

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 600));

    // In a real app, this would be an API call:
    // const response = await api.delete(`/drivers/${driverId}`, {
    //   data: { softDelete, reason }
    // });

    dispatch(deleteDriverSuccess({ driverId, softDelete, reason }));
    return { success: true };
  } catch (error) {
    dispatch(deleteDriverFailure(error.message || 'Failed to delete driver'));
    return { success: false, error: error.message };
  }
};

export const restoreDriver = (driverId) => async (dispatch) => {
  try {
    dispatch(restoreDriverStart());

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // In a real app, this would be an API call:
    // const response = await api.post(`/drivers/${driverId}/restore`);

    dispatch(restoreDriverSuccess({ driverId }));
    return { success: true };
  } catch (error) {
    dispatch(restoreDriverFailure(error.message || 'Failed to restore driver'));
    return { success: false, error: error.message };
  }
};
